import { type RefObject, useState } from "react";
import type { CropperRef } from "react-advanced-cropper";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import { useUploadMedia } from "@/shared";
import type { IMediaFile } from "@/shared/types";
import type { CropData, CropShape } from "../types";

interface IProps {
  onClose: () => void;
  imageSrc: string;

  onCrop?: (dataUrl: string, canvas: HTMLCanvasElement | null) => void;
  onCropAndUpload?: (
    mediaFile: IMediaFile,
    canvas: HTMLCanvasElement | null,
  ) => void;
  onCropDataSave?: (data: CropData) => void;
  /** When provided, the dialog closes immediately and this callback receives
   *  the blob for background upload by the caller (which stays mounted). */
  onBackgroundUpload?: (blob: Blob, blobUrl: string) => void;

  cropperRef: RefObject<CropperRef | null>;
  cropShape: CropShape;
}

const buildCropData = (
  coordinates: { left: number; top: number; width: number; height: number },
  transforms: { rotate: number; flip: { horizontal: boolean; vertical: boolean } },
  shape: CropShape,
): CropData => ({
  coordinates: { ...coordinates },
  transforms: { ...transforms },
  shape,
});

const canvasToBlob = async (canvas: HTMLCanvasElement): Promise<Blob | null> => {
  const toBlob = (type: string, quality?: number) =>
    new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), type, quality),
    );

  // WebP for smaller size and faster encoding; fall back to JPEG
  return (await toBlob("image/webp", 0.92)) ?? (await toBlob("image/jpeg", 0.92));
};

export const useCropperDialogSubmit = ({
  onClose,
  imageSrc,
  onCrop,
  onCropAndUpload,
  onCropDataSave,
  onBackgroundUpload,
  cropperRef,
  cropShape,
}: IProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { uploadMedia } = useUploadMedia();

  const save = async () => {
    if (isSaving) return;

    if (!onCrop && !onCropAndUpload) {
      onClose();
      return;
    }

    if (!cropperRef.current) return;

    const coordinates = cropperRef.current.getCoordinates();
    const image = cropperRef.current.getImage();
    const transforms = cropperRef.current.getTransforms();

    const hasTransformations =
      transforms &&
      (Math.abs((transforms.rotate || 0) % 360) > 0.1 ||
        transforms.flip.horizontal ||
        transforms.flip.vertical);

    const isFullImage =
      coordinates &&
      image &&
      Math.abs(coordinates.left) < 1 &&
      Math.abs(coordinates.top) < 1 &&
      Math.abs(coordinates.width - image.width) < 1 &&
      Math.abs(coordinates.height - image.height) < 1 &&
      !hasTransformations;

    if (coordinates && transforms) {
      onCropDataSave?.(buildCropData(coordinates, transforms, cropShape));
    }

    if (onCrop) {
      if (isFullImage) {
        onCrop(imageSrc, null as any);
      } else {
        const canvas = cropperRef.current.getCanvas();
        if (!canvas) return;
        onCrop(canvas.toDataURL("image/png"), canvas);
      }
      onClose();
      return;
    }

    if (!onCropAndUpload) {
      return;
    }

    try {
      setIsSaving(true);

      if (isFullImage && /assets(-staging)?\.storydoc\.com/.test(imageSrc)) {
        const originalUrl = imageSrc.split("?")[0];
        onCropAndUpload({ url: originalUrl } as IMediaFile, null);
        onClose();
        return;
      }

      const canvas = cropperRef.current.getCanvas();
      if (!canvas) return;

      const blob = await canvasToBlob(canvas);
      if (!blob) return;

      if (onBackgroundUpload) {
        // Apply blob URL immediately, let caller upload in background
        const blobUrl = URL.createObjectURL(blob);
        onCropAndUpload({ url: blobUrl } as IMediaFile, canvas);
        onBackgroundUpload(blob, blobUrl);
        onClose();
      } else {
        // Blocking upload: wait for upload before closing
        const mediaFile = await uploadMedia({
          blob,
          fileType: blob.type,
        });
        onCropAndUpload(mediaFile, canvas);
        onClose();
      }
    } catch (e) {
      console.log(e);
      track("failed to crop image", { error: e });
      toast.error("Failed to crop image");
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isSaving,
    save,
  };
};
